import { formatPrice } from "replo-utils/lib/format";
import { exhaustiveSwitch } from "replo-utils/lib/misc";
import * as z from "zod";

/** Wire schemas for the publisher `domains` tRPC router. */
export const registrantContactSchema = z
  .object({
    firstName: z.string().trim().min(1).max(128),
    lastName: z.string().trim().min(1).max(128),
    email: z.email().max(256),
    phone: z
      .string()
      .max(32)
      .regex(/^\+\d{1,3}\.\d+$/, "Use E.164 format, for example +1.4155550123"),
    address1: z.string().trim().min(1).max(256),
    address2: z.string().trim().max(256).nullable(),
    city: z.string().trim().min(1).max(128),
    state: z.string().trim().min(1).max(128),
    zip: z.string().trim().min(1).max(32),
    country: z.string().trim().length(2),
  })
  .describe("RegistrantContact");

export const addressSuggestionSchema = z
  .object({
    placeId: z.string(),
    label: z.string(),
  })
  .describe("AddressSuggestion");

export const autocompleteAddressOutputSchema = z
  .object({ suggestions: z.array(addressSuggestionSchema) })
  .describe("AutocompleteAddressOutput");

/** Fields are null when the resolved place record lacks that component — consumers merge per-field over what the user already typed. */
export const resolvedAddressSchema = z
  .object({
    address1: z.string().nullable(),
    address2: z.string().nullable(),
    city: z.string().nullable(),
    state: z.string().nullable(),
    zip: z.string().nullable(),
    country: z.string().nullable(),
  })
  .describe("ResolvedAddress");

export const resolveAddressOutputSchema = z
  .object({ address: resolvedAddressSchema.nullable() })
  .describe("ResolveAddressOutput");

export const domainSuggestionSchema = z
  .object({
    name: z.string(),
    available: z.boolean(),
    priceCents: z.number().nullable(),
    renewalAmount: z.number().nullable(),
    currency: z.string(),
    premium: z.boolean(),
  })
  .describe("DomainSuggestion");

export const searchDomainsOutputSchema = z
  .object({ suggestions: z.array(domainSuggestionSchema) })
  .describe("SearchDomainsOutput");

export const checkDomainOutputSchema = z
  .object({
    name: z.string(),
    available: z.boolean(),
    priceCents: z.number().nullable(),
    renewalAmount: z.number().nullable(),
    currency: z.string(),
    premium: z.boolean(),
  })
  .describe("CheckDomainOutput");

export const domainRegistrationStatusSchema = z.enum([
  "pending",
  "active",
  "suspended",
  "failed",
  "expired",
]);

/** Statuses that are real at the registrar (suspended is still owned at Cloudflare) — single source of truth for the Prisma and ark/Drizzle consumers. */
export const LIVE_DOMAIN_REGISTRATION_STATUSES = [
  "pending",
  "active",
  "suspended",
] as const satisfies readonly z.infer<typeof domainRegistrationStatusSchema>[];

// Unpaid rows block the name tenant-wide (unique on `value`); TTL exceeds Stripe Checkout's 24h max session lifetime so a session can never settle against a reclaimed reservation.
export const UNPAID_RESERVATION_TTL_MS = 48 * 60 * 60 * 1000;

// Replo owns this zone for staff test purchases: names under it skip the registrar and money but get real DNS, SSL and hosting.
export const TEST_DOMAIN_SUFFIX = ".replodomaintest.com";

export function isTestDomain(domain: string): boolean {
  return domain.toLowerCase().endsWith(TEST_DOMAIN_SUFFIX);
}

/** Holds the name until the abandonment TTL but owns nothing, so owned-domain lists skip it. */
export function isUnpaidDomainReservation(registration: {
  status: z.infer<typeof domainRegistrationStatusSchema>;
  stripeInvoiceId: string | null;
  expiresAt: Date | string | null;
}): boolean {
  return (
    registration.status === "pending" &&
    registration.stripeInvoiceId === null &&
    registration.expiresAt === null
  );
}

type OwnedDomainHealth = "live" | "working" | "attention" | "failed" | "off";

type OwnedDomainState = {
  status: z.infer<typeof domainRegistrationStatusSchema>;
  autoRenew: boolean;
  expiresAt: string | null;
};

// Paid registrations normally settle within a minute; past this, "usually under a minute" stops being true.
const STUCK_PENDING_MS = 10 * 60 * 1000;

/** Nothing writes `expired`, so a domain that won't renew is lapsed once its date passes, whatever its stored status. */
export function isOwnedDomainLapsed(domain: OwnedDomainState): boolean {
  return (
    !domain.autoRenew &&
    domain.expiresAt !== null &&
    new Date(domain.expiresAt).getTime() <= Date.now()
  );
}

/** One glance state per owned domain; auto-renew off reads as attention because it will lapse. */
export function getOwnedDomainHealth(
  domain: OwnedDomainState,
): OwnedDomainHealth {
  if (isOwnedDomainLapsed(domain)) {
    return "off";
  }
  return exhaustiveSwitch(domain.status)<OwnedDomainHealth>({
    pending: "working",
    active: domain.autoRenew ? "live" : "attention",
    suspended: "attention",
    failed: "failed",
    expired: "off",
  });
}

const OWNED_DOMAIN_DATE_FORMAT = new Intl.DateTimeFormat("en-US", {
  dateStyle: "medium",
});

/** The one line under an owned domain's name, shared by the agent panel and Settings. */
export function getOwnedDomainStatusLine(
  domain: OwnedDomainState & {
    renewalAmount: number | null;
    currency: string;
    createdAt: string;
  },
): string {
  const date = domain.expiresAt
    ? OWNED_DOMAIN_DATE_FORMAT.format(new Date(domain.expiresAt))
    : null;
  if (date && isOwnedDomainLapsed(domain)) {
    return `Expired ${date}`;
  }
  const renewal =
    domain.renewalAmount === null
      ? null
      : `${formatPrice({ amount: domain.renewalAmount, currency: domain.currency })}/year`;
  const isStuck =
    Date.now() - new Date(domain.createdAt).getTime() > STUCK_PENDING_MS;
  return exhaustiveSwitch(domain.status)<string>({
    pending: isStuck
      ? "Taking longer than usual · email support@replo.app"
      : "Registering · usually under a minute",
    active: () => {
      if (!date) {
        return "Active";
      }
      if (!domain.autoRenew) {
        return `Auto-renew off · expires ${date}`;
      }
      return renewal ? `Renews ${date} · ${renewal}` : `Renews ${date}`;
    },
    // A lapsed plan only stops routing and renewal; the registration stays the customer's until it expires.
    suspended: date
      ? `Not connected · still yours until ${date}`
      : "Not connected · still yours",
    // A visible failed row always means the automatic refund failed too; refunded ones are soft-deleted.
    failed: "Couldn't register · email support@replo.app for your refund",
    expired: date ? `Expired ${date}` : "Expired",
  });
}

export const ownedDomainSchema = z
  .object({
    id: z.string(),
    value: z.string(),
    status: domainRegistrationStatusSchema,
    expiresAt: z.string().nullable(),
    priceCents: z.number(),
    renewalAmount: z.number().nullable(),
    autoRenew: z.boolean(),
    currency: z.string(),
    createdAt: z.string(),
  })
  .describe("OwnedDomain");

export const findDomainsOutputSchema = z
  .object({ domains: z.array(ownedDomainSchema) })
  .describe("FindDomainsOutput");

export const purchaseDomainOutputSchema = z
  .object({
    domainRegistrationId: z.string(),
    value: z.string(),
    status: domainRegistrationStatusSchema,
    customDomainId: z.string(),
    liveUrl: z.string(),
  })
  .describe("PurchaseDomainOutput");

export const purchaseEligibilityOutputSchema = z
  .object({
    eligible: z.boolean(),
    reason: z.enum(["upgrade_required"]).nullable(),
  })
  .describe("PurchaseEligibilityOutput");

export const startDomainCheckoutOutputSchema = z
  .object({ confirmationUrl: z.string() })
  .describe("StartDomainCheckoutOutput");

export const domainRegistrationStatusOutputSchema = z
  .object({ status: domainRegistrationStatusSchema.nullable() })
  .describe("DomainRegistrationStatusOutput");

export const paymentMethodPreviewOutputSchema = z
  .object({
    paymentMethod: z
      .object({ brand: z.string(), last4: z.string() })
      .nullable(),
  })
  .describe("PaymentMethodPreviewOutput");

export const setAutoRenewOutputSchema = z
  .object({ autoRenew: z.boolean() })
  .describe("SetAutoRenewOutput");
